import {
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, cn, GroupedTable, Select } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import {
  DotsSixVerticalIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import {
  cellSelectClass,
  cellInputClass as inputClass,
  requiredCellProps,
} from "@/lib/utils/cell-input";
import {
  stickyHeadRuledFirstRowClass,
  stickyHeadSecondRowClass,
  stickyRowHeaderContentClass,
  useStickyHeaderOffsets,
} from "@/lib/utils/sticky-table-header";

export type GeoZoneType = "horizontale" | "verticale";

export type GeoZone = {
  id: number;
  buildingId: number;
  name: string;
  shortName: string | null;
  type: GeoZoneType;
  order: number;
};

export type GeoBuilding = {
  id: number;
  name: string;
  shortName: string | null;
  order: number;
  /** Zones already sorted by type then display order. */
  zones: GeoZone[];
};

export const ZONE_TYPE_LABELS: Record<GeoZoneType, string> = {
  horizontale: "Horizontale",
  verticale: "Verticale",
};

const ZONE_TYPE_ORDER: GeoZoneType[] = ["horizontale", "verticale"];

/**
 * Minimum width of the building / zone "Désignation" columns (~30 characters,
 * per .agents/screen-column-widths.md, screen 904), so the auto table layout
 * cannot squeeze them until names get cut off. Longer names end in an
 * ellipsis and show in full on hover.
 */
const DESIGNATION_COLUMN_CLASS = "min-w-56";

const zoneTypeSelectOptions = ZONE_TYPE_ORDER.map((type) => ({
  label: ZONE_TYPE_LABELS[type],
  value: type,
}));

/**
 * `closestCenter` measures from the middle of the dragged block: a tall building
 * stayed closer to its own slot than to a short one above it. The building under
 * the pointer wins. With no pointer (keyboard) or off the table, the nearest top
 * or bottom edge does: the keyboard lines those up with the target's.
 */
export const buildingCollision: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args);
  if (underPointer.length > 0) return underPointer;
  const { collisionRect, droppableContainers, droppableRects } = args;
  return droppableContainers
    .flatMap((droppableContainer) => {
      const rect = droppableRects.get(droppableContainer.id);
      if (isNil(rect)) return [];
      const value = Math.min(
        Math.abs(collisionRect.top - rect.top),
        Math.abs(collisionRect.bottom - rect.bottom),
      );
      return [
        { id: droppableContainer.id, data: { droppableContainer, value } },
      ];
    })
    .sort((a, b) => a.data.value - b.data.value);
};

export type BuildingDraft = { name: string; shortName: string };
export type ZoneDraft = { name: string; shortName: string; type: GeoZoneType };
export type NewBuildingRow = { tempId: string } & BuildingDraft;
export type NewZoneRow = { tempId: string } & ZoneDraft;

export type YardGeographyTableProps = {
  /**
   * Draft status and "Annuler" / "Enregistrer" buttons, shown while the draft
   * is dirty. Rendered in the always-present toolbar row next to "Ajouter un
   * bâtiment" so showing or hiding them never shifts the table.
   */
  draftActions?: ReactNode;
  buildings: GeoBuilding[];
  getBuildingDraft: (building: GeoBuilding) => BuildingDraft;
  isBuildingDirty: (building: GeoBuilding) => boolean;
  onBuildingFieldChange: (
    building: GeoBuilding,
    patch: Partial<BuildingDraft>,
  ) => void;
  getZoneDraft: (zone: GeoZone) => ZoneDraft;
  isZoneDirty: (zone: GeoZone) => boolean;
  onZoneFieldChange: (zone: GeoZone, patch: Partial<ZoneDraft>) => void;
  onDeleteBuilding: (building: GeoBuilding) => void;
  onDeleteZone: (zone: GeoZone) => void;
  onReorderBuildings: (orderedIds: number[]) => void;
  onReorderZones: (
    buildingId: number,
    type: GeoZoneType,
    orderedIds: number[],
  ) => void;
  newBuildings: NewBuildingRow[];
  onNewBuildingFieldChange: (
    tempId: string,
    patch: Partial<BuildingDraft>,
  ) => void;
  onRemoveNewBuilding: (tempId: string) => void;
  onAddBuildingRow: () => void;
  newZonesByBuilding: Map<number, NewZoneRow[]>;
  onNewZoneFieldChange: (
    buildingId: number,
    tempId: string,
    patch: Partial<ZoneDraft>,
  ) => void;
  onRemoveNewZone: (buildingId: number, tempId: string) => void;
  onAddZoneRow: (buildingId: number) => void;
};

function DragHandle(props: {
  attributes: React.HTMLAttributes<HTMLButtonElement>;
  listeners: Record<string, unknown> | undefined;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={props.label}
      className="cursor-grab touch-none text-zinc-400 hover:text-zinc-600 active:cursor-grabbing dark:hover:text-zinc-200"
      {...props.attributes}
      {...props.listeners}
    >
      <DotsSixVerticalIcon className="size-5" />
    </button>
  );
}

/**
 * A saved zone row. `leading` carries the building cells (with rowSpan) when
 * this is the first row of the building's block, per the wireframe where the
 * building line is shared with its first zone.
 */
function SortableZoneRow({
  zone,
  draft,
  dirty,
  displayOrder,
  onFieldChange,
  onDelete,
  leading,
}: {
  zone: GeoZone;
  draft: ZoneDraft;
  dirty: boolean;
  displayOrder: number;
  onFieldChange: (patch: Partial<ZoneDraft>) => void;
  onDelete: (zone: GeoZone) => void;
  leading?: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `zone-${zone.id}` });

  // The drag transform goes on the zone cells, not the <tr>: the first zone
  // row also hosts the building cells (rowSpan), which must stay put while
  // zones are reordered.
  const cellStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <GroupedTable.Row ref={setNodeRef}>
      {leading}
      <GroupedTable.Cell className="w-8" style={cellStyle}>
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          label={`Déplacer la zone ${zone.name}`}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className={DESIGNATION_COLUMN_CLASS} style={cellStyle}>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => onFieldChange({ name: e.target.value })}
          className={inputClass(dirty)}
          {...requiredCellProps("Désignation", draft.name, dirty)}
          title={draft.name}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-32" style={cellStyle}>
        <input
          type="text"
          value={draft.shortName}
          onChange={(e) => onFieldChange({ shortName: e.target.value })}
          className={inputClass(dirty)}
          placeholder="Abréviation"
          title={draft.shortName}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell
        className="w-16 text-center text-zinc-500"
        style={cellStyle}
      >
        {displayOrder}
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-40" style={cellStyle}>
        <Select
          flat
          className={cellSelectClass(dirty)}
          options={zoneTypeSelectOptions}
          value={[draft.type]}
          onValueChange={(details) => {
            const value = details.value[0];
            if (value) onFieldChange({ type: value as GeoZoneType });
          }}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-12" style={cellStyle}>
        <Button
          plain
          onClick={() => onDelete(zone)}
          aria-label="Supprimer la zone"
        >
          <TrashIcon className="size-4" />
        </Button>
      </GroupedTable.Cell>
    </GroupedTable.Row>
  );
}

/** A not-yet-saved zone row (added via "+", pending "Enregistrer"). */
function NewZoneRowView({
  row,
  onFieldChange,
  onRemove,
  leading,
}: {
  row: NewZoneRow;
  onFieldChange: (patch: Partial<ZoneDraft>) => void;
  onRemove: () => void;
  leading?: ReactNode;
}) {
  return (
    <GroupedTable.Row>
      {leading}
      <GroupedTable.Cell className="w-8" />
      <GroupedTable.Cell className={DESIGNATION_COLUMN_CLASS}>
        <input
          type="text"
          value={row.name}
          onChange={(e) => onFieldChange({ name: e.target.value })}
          className={inputClass(true)}
          {...requiredCellProps("Désignation", row.name, true)}
          title={row.name}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-32">
        <input
          type="text"
          value={row.shortName}
          onChange={(e) => onFieldChange({ shortName: e.target.value })}
          className={inputClass(true)}
          placeholder="Abréviation"
          title={row.shortName}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-16 text-center text-zinc-400">
        —
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-40">
        <Select
          flat
          className={cellSelectClass(true)}
          options={zoneTypeSelectOptions}
          value={[row.type]}
          onValueChange={(details) => {
            const value = details.value[0];
            if (value) onFieldChange({ type: value as GeoZoneType });
          }}
        />
      </GroupedTable.Cell>
      <GroupedTable.Cell className="w-12">
        <Button
          plain
          onClick={onRemove}
          aria-label="Annuler l'ajout de la zone"
        >
          <XIcon className="size-4" />
        </Button>
      </GroupedTable.Cell>
    </GroupedTable.Row>
  );
}

/**
 * Zones of one building+type group, sortable among themselves only.
 */
function ZoneDndGroup({
  buildingId,
  type,
  zones,
  firstRowLeading,
  getZoneDraft,
  isZoneDirty,
  onZoneFieldChange,
  onDeleteZone,
  onReorderZones,
}: {
  buildingId: number;
  type: GeoZoneType;
  zones: GeoZone[];
  /** Building cells to prepend to the first row of this group, if any. */
  firstRowLeading?: ReactNode;
  getZoneDraft: (zone: GeoZone) => ZoneDraft;
  isZoneDirty: (zone: GeoZone) => boolean;
  onZoneFieldChange: (zone: GeoZone, patch: Partial<ZoneDraft>) => void;
  onDeleteZone: (zone: GeoZone) => void;
  onReorderZones: (
    buildingId: number,
    type: GeoZoneType,
    orderedIds: number[],
  ) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = zones.map((z) => z.id);
    const oldIndex = ids.indexOf(
      Number(String(active.id).replace("zone-", "")),
    );
    const newIndex = ids.indexOf(Number(String(over.id).replace("zone-", "")));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderZones(buildingId, type, arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      // Portal dnd-kit's hidden accessibility nodes out of the <tbody>,
      // where raw <div>s are invalid HTML.
      accessibility={{ container: document.body }}
    >
      <SortableContext
        items={zones.map((z) => `zone-${z.id}`)}
        strategy={verticalListSortingStrategy}
      >
        {zones.map((zone, index) => (
          <SortableZoneRow
            key={zone.id}
            zone={zone}
            draft={getZoneDraft(zone)}
            dirty={isZoneDirty(zone)}
            displayOrder={index + 1}
            onFieldChange={(patch) => onZoneFieldChange(zone, patch)}
            onDelete={onDeleteZone}
            leading={index === 0 ? firstRowLeading : undefined}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

/**
 * One building block: a sortable <tbody> whose building cells span all the
 * zone rows (wireframe layout), followed by an "add zone" row.
 */
function SortableBuildingBody({
  building,
  displayOrder,
  draft,
  dirty,
  onFieldChange,
  onDeleteBuilding,
  getZoneDraft,
  isZoneDirty,
  onZoneFieldChange,
  onDeleteZone,
  onReorderZones,
  newZones,
  onNewZoneFieldChange,
  onRemoveNewZone,
  onAddZoneRow,
}: {
  building: GeoBuilding;
  displayOrder: number;
  draft: BuildingDraft;
  dirty: boolean;
  onFieldChange: (patch: Partial<BuildingDraft>) => void;
  onDeleteBuilding: (building: GeoBuilding) => void;
  getZoneDraft: (zone: GeoZone) => ZoneDraft;
  isZoneDirty: (zone: GeoZone) => boolean;
  onZoneFieldChange: (zone: GeoZone, patch: Partial<ZoneDraft>) => void;
  onDeleteZone: (zone: GeoZone) => void;
  onReorderZones: (
    buildingId: number,
    type: GeoZoneType,
    orderedIds: number[],
  ) => void;
  newZones: NewZoneRow[];
  onNewZoneFieldChange: (tempId: string, patch: Partial<ZoneDraft>) => void;
  onRemoveNewZone: (tempId: string) => void;
  onAddZoneRow: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `building-${building.id}` });

  const zonesByType = ZONE_TYPE_ORDER.map((type) => ({
    type,
    zones: building.zones.filter((z) => z.type === type),
  })).filter((group) => group.zones.length > 0);

  const zoneRowCount = building.zones.length + newZones.length;
  // Building cells span every zone row plus the trailing "add zone" row
  // (or the single "Aucune zone" row when the building has no zones yet).
  const totalRows = Math.max(1, zoneRowCount) + 1;

  const buildingCells = (
    <>
      <GroupedTable.Cell rowSpan={totalRows} className="w-8 align-top">
        {/* Same height as the first zone row's inputs, so the handle and
            order line up with it. */}
        <div
          className={cn(stickyRowHeaderContentClass, "flex h-8 items-center")}
        >
          <DragHandle
            attributes={attributes}
            listeners={listeners}
            label={`Déplacer le bâtiment ${building.name}`}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell
        rowSpan={totalRows}
        className="w-16 text-center align-top text-zinc-500"
      >
        <div
          className={cn(
            stickyRowHeaderContentClass,
            "flex h-8 items-center justify-center",
          )}
        >
          {displayOrder}
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell
        rowSpan={totalRows}
        className={cn(DESIGNATION_COLUMN_CLASS, "align-top")}
      >
        <div className={stickyRowHeaderContentClass}>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => onFieldChange({ name: e.target.value })}
            className={inputClass(dirty)}
            {...requiredCellProps("Désignation", draft.name, dirty)}
            title={draft.name}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell rowSpan={totalRows} className="w-32 align-top">
        <div className={stickyRowHeaderContentClass}>
          <input
            type="text"
            value={draft.shortName}
            onChange={(e) => onFieldChange({ shortName: e.target.value })}
            className={inputClass(dirty)}
            {...requiredCellProps("Abréviation", draft.shortName, dirty)}
            title={draft.shortName}
          />
        </div>
      </GroupedTable.Cell>
      <GroupedTable.Cell rowSpan={totalRows} className="w-12 align-top">
        <div className={stickyRowHeaderContentClass}>
          <Button
            plain
            onClick={() => onDeleteBuilding(building)}
            aria-label="Supprimer le bâtiment"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      </GroupedTable.Cell>
    </>
  );

  // The building cells are attached to the first rendered zone row (saved
  // zone, else pending new zone), or to the "Aucune zone" placeholder row.
  const firstGroupType = zonesByType[0]?.type;

  return (
    <tbody
      ref={setNodeRef}
      style={{
        // Translate only: dnd-kit scales the dragged item to the size of the
        // one under it, and buildings have different heights.
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      className="border-b border-gray-200 last:border-0"
    >
      {zoneRowCount === 0 && (
        <GroupedTable.Row>
          {buildingCells}
          <GroupedTable.Cell colSpan={6} className="text-zinc-400">
            Aucune zone
          </GroupedTable.Cell>
        </GroupedTable.Row>
      )}

      {zonesByType.map((group) => (
        <ZoneDndGroup
          key={group.type}
          buildingId={building.id}
          type={group.type}
          zones={group.zones}
          firstRowLeading={
            group.type === firstGroupType ? buildingCells : undefined
          }
          getZoneDraft={getZoneDraft}
          isZoneDirty={isZoneDirty}
          onZoneFieldChange={onZoneFieldChange}
          onDeleteZone={onDeleteZone}
          onReorderZones={onReorderZones}
        />
      ))}

      {newZones.map((row, index) => (
        <NewZoneRowView
          key={row.tempId}
          row={row}
          onFieldChange={(patch) => onNewZoneFieldChange(row.tempId, patch)}
          onRemove={() => onRemoveNewZone(row.tempId)}
          leading={
            building.zones.length === 0 && index === 0
              ? buildingCells
              : undefined
          }
        />
      ))}

      <GroupedTable.Row>
        <GroupedTable.Cell colSpan={6} className="py-1">
          {/* `!`: Button applies its own padding after className. */}
          <Button plain className="sm:py-0.5!" onClick={onAddZoneRow}>
            <PlusIcon data-slot="icon" className="size-4" />
            Ajouter une zone
          </Button>
        </GroupedTable.Cell>
      </GroupedTable.Row>
    </tbody>
  );
}

/** A not-yet-saved building (added via "+", pending "Enregistrer"). */
function NewBuildingBody({
  row,
  onFieldChange,
  onRemove,
}: {
  row: NewBuildingRow;
  onFieldChange: (patch: Partial<BuildingDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <tbody className="border-b border-gray-200 last:border-0">
      <GroupedTable.Row className="bg-amber-50/50 dark:bg-amber-900/10">
        <GroupedTable.Cell className="w-8" />
        <GroupedTable.Cell className="w-16 text-center text-zinc-400">
          —
        </GroupedTable.Cell>
        <GroupedTable.Cell className={DESIGNATION_COLUMN_CLASS}>
          <input
            type="text"
            value={row.name}
            onChange={(e) => onFieldChange({ name: e.target.value })}
            className={inputClass(true)}
            {...requiredCellProps("Désignation", row.name, true)}
            title={row.name}
          />
        </GroupedTable.Cell>
        <GroupedTable.Cell className="w-32">
          <input
            type="text"
            value={row.shortName}
            onChange={(e) => onFieldChange({ shortName: e.target.value })}
            className={inputClass(true)}
            {...requiredCellProps("Abréviation", row.shortName, true)}
            title={row.shortName}
          />
        </GroupedTable.Cell>
        <GroupedTable.Cell className="w-12">
          <Button
            plain
            onClick={onRemove}
            aria-label="Annuler l'ajout du bâtiment"
          >
            <XIcon className="size-4" />
          </Button>
        </GroupedTable.Cell>
        <GroupedTable.Cell colSpan={6} className="text-xs text-zinc-400">
          Enregistrez ce bâtiment pour pouvoir y ajouter des zones.
        </GroupedTable.Cell>
      </GroupedTable.Row>
    </tbody>
  );
}

export function YardGeographyTable({
  draftActions,
  buildings,
  getBuildingDraft,
  isBuildingDirty,
  onBuildingFieldChange,
  getZoneDraft,
  isZoneDirty,
  onZoneFieldChange,
  onDeleteBuilding,
  onDeleteZone,
  onReorderBuildings,
  onReorderZones,
  newBuildings,
  onNewBuildingFieldChange,
  onRemoveNewBuilding,
  onAddBuildingRow,
  newZonesByBuilding,
  onNewZoneFieldChange,
  onRemoveNewZone,
  onAddZoneRow,
}: YardGeographyTableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = buildings.map((b) => b.id);
    const oldIndex = ids.indexOf(
      Number(String(active.id).replace("building-", "")),
    );
    const newIndex = ids.indexOf(
      Number(String(over.id).replace("building-", "")),
    );
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderBuildings(arrayMove(ids, oldIndex, newIndex));
  };

  const { headerRef, style: stickyOffsets } = useStickyHeaderOffsets();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-end gap-2">
        {draftActions}
        <Button onClick={onAddBuildingRow}>
          <PlusIcon data-slot="icon" className="size-4" />
          Ajouter un bâtiment
        </Button>
      </div>

      {buildings.length === 0 && newBuildings.length === 0 ? (
        <div className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-zinc-300 text-zinc-500 dark:border-zinc-700">
          Aucun bâtiment. Commencez par en ajouter un.
        </div>
      ) : (
        // Sole scroll region of the screen (screen-design.md): shrinks to the
        // remaining height, scrolls its rows under a sticky header. Dense rows
        // with tight cell padding fit more lines on screen. The cell height
        // is that of a zone row, so text-only rows match it.
        <GroupedTable
          grid
          dense
          className="min-h-0 [&_td]:h-9.75 [&_td]:py-1"
          style={stickyOffsets}
        >
          <GroupedTable.Header ref={headerRef}>
            <GroupedTable.HeaderRow>
              <GroupedTable.HeaderGroup
                colSpan={5}
                className={stickyHeadRuledFirstRowClass}
              >
                Bâtiments
              </GroupedTable.HeaderGroup>
              <GroupedTable.HeaderGroup
                colSpan={6}
                className={stickyHeadRuledFirstRowClass}
              >
                Zones associées
              </GroupedTable.HeaderGroup>
            </GroupedTable.HeaderRow>
            <GroupedTable.HeaderRow>
              {/* Bâtiments */}
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-8")}
              />
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-16 text-center")}
              >
                Ordre
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(
                  stickyHeadSecondRowClass,
                  DESIGNATION_COLUMN_CLASS,
                )}
              >
                Désignation
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-32")}
              >
                Abréviation
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-12")}
              />
              {/* Zones associées */}
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-8")}
              />
              <GroupedTable.Head
                className={cn(
                  stickyHeadSecondRowClass,
                  DESIGNATION_COLUMN_CLASS,
                )}
              >
                Désignation
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-32")}
              >
                Abréviation
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-16 text-center")}
              >
                Ordre
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-40")}
              >
                Type
              </GroupedTable.Head>
              <GroupedTable.Head
                className={cn(stickyHeadSecondRowClass, "w-12")}
              />
            </GroupedTable.HeaderRow>
          </GroupedTable.Header>

          <DndContext
            sensors={sensors}
            collisionDetection={buildingCollision}
            onDragEnd={handleDragEnd}
            // Portal dnd-kit's hidden accessibility nodes out of the <table>,
            // where raw <div>s are invalid HTML.
            accessibility={{ container: document.body }}
          >
            <SortableContext
              items={buildings.map((b) => `building-${b.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {buildings.map((building, index) => (
                <SortableBuildingBody
                  key={building.id}
                  building={building}
                  displayOrder={index + 1}
                  draft={getBuildingDraft(building)}
                  dirty={isBuildingDirty(building)}
                  onFieldChange={(patch) =>
                    onBuildingFieldChange(building, patch)
                  }
                  onDeleteBuilding={onDeleteBuilding}
                  getZoneDraft={getZoneDraft}
                  isZoneDirty={isZoneDirty}
                  onZoneFieldChange={onZoneFieldChange}
                  onDeleteZone={onDeleteZone}
                  onReorderZones={onReorderZones}
                  newZones={newZonesByBuilding.get(building.id) ?? []}
                  onNewZoneFieldChange={(tempId, patch) =>
                    onNewZoneFieldChange(building.id, tempId, patch)
                  }
                  onRemoveNewZone={(tempId) =>
                    onRemoveNewZone(building.id, tempId)
                  }
                  onAddZoneRow={() => onAddZoneRow(building.id)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {newBuildings.map((row) => (
            <NewBuildingBody
              key={row.tempId}
              row={row}
              onFieldChange={(patch) =>
                onNewBuildingFieldChange(row.tempId, patch)
              }
              onRemove={() => onRemoveNewBuilding(row.tempId)}
            />
          ))}
        </GroupedTable>
      )}
    </div>
  );
}
